import { MeasurementScale } from "@repo/app-types/entities/interview";
import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { PillDash } from "@repo/ui/Pills/pillDash";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import type { FC } from "react";
import { AssessmentScore } from "../insights/assessmentScore.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";
import { renderPercentage } from "./percentagePill.js";

//
// Props.
//

interface CriterionAverageScoreProps {
  valueType: PlaybookCriterionNode["grading_value_type"];
  /** `undefined` means the score is absent, and renders as a muted `--`. */
  value: number | undefined;
  /** Renders a skeleton instead of the `--`, while there is no score yet. */
  isLoading?: boolean;
  /** Size of the pill/score display. Defaults to SMALL. */
  size?: Size;
  onClick?: () => void;
  /**
   * Sizes the pill to the widest value any criterion score shows, which is a
   * full pass rate — wider than the top of the 1-5 scale — so that a column
   * mixing the two lines up.
   */
  sizedToMax?: boolean;
}

//
// Component.
//

export const CriterionAverageScore: FC<CriterionAverageScoreProps> = (
  props,
) => {
  const { valueType, value, size = Size.SMALL, sizedToMax } = props;

  if (typeof value === "undefined") {
    return props.isLoading ? (
      <PillSkeleton size={size} />
    ) : (
      <PillDash size={size} />
    );
  }

  // Size both branches to a pass rate, rather than letting each size to its
  // own maximum: a 1-5 average would come out narrower than the pass rates on
  // the rows around it.
  const childrenForSize = sizedToMax ? renderPercentage(1) : null;

  switch (valueType) {
    // For a 1-5 score, value is an average.
    case "1_5_score":
      return (
        <AssessmentScore
          size={size}
          score={{
            scale: MeasurementScale.FivePointInterval,
            value,
          }}
          onClick={props.onClick}
          childrenForSize={childrenForSize}
        />
      );

    // For a yes-no score, value is a pass rate.
    case "yes_no":
      return (
        <GradingPassRatePill
          size={size}
          passRate={value}
          onClick={props.onClick}
          childrenForSize={childrenForSize}
        />
      );

    default:
      assertUnreachable(valueType);
  }
};
